import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cartAPI, promotionsAPI, userAPI, shippingAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { getCheckoutFeeRates, calculateLogisticsVat, calculateEscrowFee, DEFAULT_VAT_PERCENTAGE, DEFAULT_ESCROW_FEE_PERCENTAGE, APPLIED_PROMO_STORAGE_KEY } from '../utils/vat';
import '../assets/css/styles.css';
import '../assets/css/cart.css';
import { useNotifications } from '../context/NotificationContext';

const Cart = () => {
  const { showCustomToast } = useNotifications();
  const navigate = useNavigate();
  const { refreshCart } = useCart();
  const [cartItems, setCartItems] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [savedForLater, setSavedForLater] = useState([]);

  // Store full backend cart response (matching mobile app pattern)
  const [cartData, setCartData] = useState({});

  // Real FabsTech quote for the buyer's default saved address, fetched the
  // same way Checkout.jsx does — set once resolved so this page can show
  // the same number Checkout will, instead of always guessing with a flat
  // placeholder. Stays null (falls back to FLAT_SHIPPING_RATE below) for a
  // guest or a buyer with no saved address, since there's nothing real to quote yet.
  const [realShippingFee, setRealShippingFee] = useState(null);

  // There is no free-shipping tier — every order carries a real logistics
  // charge, quoted by the logistics API at checkout. This flat rate is only
  // the pre-checkout estimate shown here before a delivery address is known.
  // Must match Checkout.jsx's own pre-quote fallback (its initial
  // shippingFee state, and its fetchShippingRate catch-block default) and
  // the backend's own FabsTech-failure fallback (handlers/shipping.go) — all
  // three are meant to be the same "no real quote yet" placeholder, and
  // having Cart show a different number here just relabels the same bug the
  // rest of the checkout flow already had.
  const FLAT_SHIPPING_RATE = 5000;
  const [vatPercentage, setVatPercentage] = useState(DEFAULT_VAT_PERCENTAGE);
  const [escrowFeePercentage, setEscrowFeePercentage] = useState(DEFAULT_ESCROW_FEE_PERCENTAGE);

  // VAT rate applied to the logistics/shipping fee only, and the Escrow Fee
  // rate — see src/utils/vat.js. Checkout defaults use_escrow to true (the
  // buyer can opt out there), so showing this estimate here too means the
  // cart total isn't missing a fee most buyers will actually be charged.
  useEffect(() => {
    getCheckoutFeeRates().then(({ vatPercentage, escrowFeePercentage }) => {
      setVatPercentage(vatPercentage);
      setEscrowFeePercentage(escrowFeePercentage);
    });
  }, []);

  // Load saved for later items from localStorage on mount
  useEffect(() => {
    const savedItems = localStorage.getItem('autoboy_saved_for_later');
    if (savedItems) {
      try {
        setSavedForLater(JSON.parse(savedItems));
      } catch (e) {
        setSavedForLater([]);
      }
    }
  }, []);
  

  // Fetch cart from backend on component mount (matching mobile app pattern)
  useEffect(() => {
    const fetchCart = async () => {
      // Check if user is authenticated
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsLoading(false);
        setCartItems([]);
        return;
      }

      try {
        setIsLoading(true);
        const response = await cartAPI.getCart();

        if (response && response.data) {
          // Store full backend response (matching mobile app: cart.tsx line 44)
          setCartData(response.data || {});

          // Handle different response formats
          const items = response.data.items || response.data.cart?.items || response.data || [];

          if (!Array.isArray(items)) {
            setCartItems([]);
            return;
          }

          // Transform backend cart items to match component format
          const transformedItems = items.map(item => {

            // Extract image from product images array (matching mobile app: cart.tsx line 191)
            let productImage = 'https://via.placeholder.com/200?text=No+Image';

            if (item.product?.images && item.product.images.length > 0) {
              const firstImage = item.product.images[0];
              // Handle both string and object with url property
              productImage = typeof firstImage === 'string' ? firstImage : (firstImage?.url || firstImage?.URL);
            } else if (item.images && item.images.length > 0) {
              const firstImage = item.images[0];
              productImage = typeof firstImage === 'string' ? firstImage : (firstImage?.url || firstImage?.URL);
            } else if (item.product?.image) {
              productImage = item.product.image;
            } else if (item.image) {
              productImage = item.image;
            }

            return {
              id: item._id || item.id,
              product_id: item.product_id || item.product?._id,
              name: item.product?.title || item.product?.name || item.name || 'Product',
              description: item.product?.description || item.description || '',
              // Server-resolved line price wins over the product's retail
              // price — sellers are quoted (and charged) wholesale.
              price: item.price ?? item.product?.price ?? 0,
              quantity: item.quantity || 1,
              image: productImage,
              status: item.product?.status,
              stock: item.product?.quantity || item.product?.stock || item.product?.stock_count,
              selected: true  // Add selection state (matching mobile app: cart.tsx line 110)
            };
          });

          setCartItems(transformedItems);
        } else {
          setCartItems([]);
        }
      } catch (error) {

        // Check if it's an auth error
        if (error.isAuthError || error.message.includes('session has expired')) {
          // The API client will handle redirect
        } else if (error.message.includes('CORS') || error.message.includes('Network error')) {
          // Don't alert on CORS - just show empty state
          setCartItems([]);
        } else {
          // Only alert for non-CORS errors
          if (!error.message.includes('Failed to fetch')) {
            showCustomToast({ type: 'warning', message: 'Unable to load cart. Please check your connection and try refreshing the page.' });
          }
        }

        setCartItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCart();
  }, []);

  // If the buyer has a saved default address, quote real shipping for it —
  // the exact same call Checkout.jsx's fetchShippingRate makes — so this
  // page shows the real number instead of a flat guess whenever we actually
  // have enough information to compute one. Depends on cartItems since the
  // request needs the actual product IDs/quantities being priced.
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token || cartItems.length === 0) return;

    const fetchRealShippingForDefaultAddress = async () => {
      try {
        const addressesResponse = await userAPI.getAddresses();
        const addresses = addressesResponse?.data?.addresses || addressesResponse?.data || [];
        if (!Array.isArray(addresses) || addresses.length === 0) return;

        const addr = addresses.find(a => a.isDefault || a.is_default) || addresses[0];
        if (!addr?.street || !addr?.city || !addr?.state) return;

        const deliveryAddress = `${addr.street}, ${addr.city}, ${addr.state}, ${addr.country || 'Nigeria'}`;
        const items = cartItems.map(i => ({ product_id: i.product_id, quantity: i.quantity }));

        const response = await shippingAPI.calculateShipping({ address: deliveryAddress, items });
        const data = response?.data?.data || response?.data;
        if (data?.shipping_fee != null && data?.estimated === false) {
          setRealShippingFee(data.shipping_fee);
        }
      } catch {
        // Leave realShippingFee null — calculateShipping() falls back to the flat estimate.
      }
    };

    fetchRealShippingForDefaultAddress();
  }, [cartItems.length]);

  const updateQuantity = async (id, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      // Optimistically update UI
      setCartItems(items =>
        items.map(item =>
          item.id === id ? { ...item, quantity: newQuantity } : item
        )
      );

      // Update backend
      await cartAPI.updateItem(id, newQuantity);

      // Refresh cart count in navbar
      refreshCart();
    } catch (error) {
      showCustomToast({ type: 'warning', message: 'Failed to update quantity. Please try again.' });
      // Revert on error - refetch cart
      const response = await cartAPI.getCart();
      if (response && response.data) {
        const items = response.data.items || response.data.cart?.items || [];
        const transformedItems = items.map(item => {
          let productImage = 'https://via.placeholder.com/200?text=No+Image';
          if (item.product?.images && item.product.images.length > 0) {
            const firstImage = item.product.images[0];
            productImage = typeof firstImage === 'string' ? firstImage : firstImage?.url;
          } else if (item.product?.image) {
            productImage = item.product.image;
          }

          return {
            id: item._id || item.id,
            product_id: item.product_id,
            name: item.product?.title || item.product?.name || item.name,
            description: item.product?.description || item.description,
            price: item.price ?? item.product?.price,
            quantity: item.quantity,
            image: productImage
          };
        });
        setCartItems(transformedItems);
      }
    }
  };

  const toggleSelection = (id) => {
    // Toggle item selection (matching mobile app: cart.tsx line 108-117)
    setCartItems(items =>
      items.map(item =>
        item.id === id ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const removeItem = async (id) => {
    if (!window.confirm('Are you sure you want to remove this item from your cart?')) return;

    try {
      // Optimistically remove from UI
      setCartItems(items => items.filter(item => item.id !== id));

      // Remove from backend
      await cartAPI.removeItem(id);

      // Refresh cart count in navbar
      refreshCart();
    } catch (error) {
      showCustomToast({ type: 'warning', message: 'Failed to remove item. Please try again.' });
      // Revert on error - refetch cart
      const response = await cartAPI.getCart();
      if (response && response.data) {
        const items = response.data.items || response.data.cart?.items || [];
        const transformedItems = items.map(item => {
          let productImage = 'https://via.placeholder.com/200?text=No+Image';
          if (item.product?.images && item.product.images.length > 0) {
            const firstImage = item.product.images[0];
            productImage = typeof firstImage === 'string' ? firstImage : firstImage?.url;
          } else if (item.product?.image) {
            productImage = item.product.image;
          }

          return {
            id: item._id || item.id,
            product_id: item.product_id,
            name: item.product?.title || item.product?.name || item.name,
            description: item.product?.description || item.description,
            price: item.price ?? item.product?.price,
            quantity: item.quantity,
            image: productImage
          };
        });
        setCartItems(transformedItems);
      }
    }
  };

  const saveForLater = (id) => {
    const item = cartItems.find(item => item.id === id);
    if (item) {
      const updatedSaved = [...savedForLater, item];
      setSavedForLater(updatedSaved);
      setCartItems(items => items.filter(item => item.id !== id));

      // Persist to localStorage
      localStorage.setItem('autoboy_saved_for_later', JSON.stringify(updatedSaved));
    }
  };

  const moveToCart = (id) => {
    const item = savedForLater.find(item => item.id === id);
    if (item) {
      setCartItems(prev => [...prev, item]);
      const updatedSaved = savedForLater.filter(item => item.id !== id);
      setSavedForLater(updatedSaved);

      // Update localStorage
      localStorage.setItem('autoboy_saved_for_later', JSON.stringify(updatedSaved));
    }
  };

  const removeSavedItem = (id) => {
    const updatedSaved = savedForLater.filter(item => item.id !== id);
    setSavedForLater(updatedSaved);

    // Update localStorage
    localStorage.setItem('autoboy_saved_for_later', JSON.stringify(updatedSaved));
  };

  const applyPromoCode = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    const subtotal = calculateSubtotal();
    if (subtotal <= 0) {
      showCustomToast({ type: 'warning', message: 'Select at least one item before applying a promo code' });
      return;
    }

    setApplyingPromo(true);
    try {
      const res = await promotionsAPI.validate(code, subtotal);
      const data = res?.data || res;
      const promo = { code: data.code || code, type: data.type, discount_amount: data.discount_amount || 0, description: data.description || '' };
      setAppliedPromo(promo);
      sessionStorage.setItem(APPLIED_PROMO_STORAGE_KEY, JSON.stringify(promo));
      setPromoCode('');
      showCustomToast({ type: 'success', message: promo.description || `Promo code ${promo.code} applied` });
    } catch (error) {
      showCustomToast({ type: 'warning', message: error.message || 'Invalid or expired promo code' });
    } finally {
      setApplyingPromo(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    sessionStorage.removeItem(APPLIED_PROMO_STORAGE_KEY);
  };

  const calculateSubtotal = () => {
    // Calculate from selected items only (matching mobile app: cart.tsx line 148-155)
    const selectedItems = cartItems.filter(item => item.selected);
    return selectedItems.reduce((total, item) => {
      const price = item.price || 0;
      const quantity = item.quantity || 1;
      return total + (price * quantity);
    }, 0);
  };

  // The discount amount is computed and returned by the backend (POST
  // /promo-codes/validate) at apply time — no client-side re-derivation of
  // percentage/fixed math, so this can never drift from what the backend
  // will actually charge at order creation.
  const calculateDiscount = () => appliedPromo?.discount_amount || 0;

  const calculateShipping = () => {
    const subtotal = calculateSubtotal();

    if (subtotal === 0) return 0;

    // A real FabsTech quote for the buyer's default address, if we managed
    // to fetch one — see the effect above. This is what makes the total
    // here match what Checkout shows, instead of two different guesses.
    if (realShippingFee != null) {
      return realShippingFee;
    }

    // Use backend shipping value (matching mobile app: cart.tsx line 157)
    // Field name is "shipping" NOT "shipping_cost"
    if (cartData.shipping) {
      return cartData.shipping;
    }

    // Estimate — no saved address to quote against yet (guest, or a buyer
    // with no address on file). Matches Checkout's own pre-quote fallback.
    return FLAT_SHIPPING_RATE;
  };

  // Logistics VAT — VAT on the shipping fee only, never on the product
  // price. No general buyer-facing product tax exists; VAT is otherwise
  // only charged to the seller, on their commission.
  const calculateLogisticsVatAmount = () => calculateLogisticsVat(calculateShipping(), vatPercentage);

  // The combined figure shown to the buyer: shipping fee + VAT on it.
  const calculateLogisticsCharge = () => calculateShipping() + calculateLogisticsVatAmount();

  // Escrow protection is on by default at Checkout (the buyer can uncheck
  // it there) — estimating it here too, on the same subtotal/rate Checkout
  // uses, means this total isn't missing a charge most buyers will see.
  const calculateEscrowFeeAmount = () => calculateEscrowFee(calculateSubtotal(), escrowFeePercentage);

  const calculateTotal = () => {
    // subtotal - discount + logistics charge (shipping + its VAT) + escrow fee
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const logisticsCharge = calculateLogisticsCharge();
    const escrowFee = calculateEscrowFeeAmount();

    return subtotal - discount + logisticsCharge + escrowFee;
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleProceedToCheckout = () => {
    if (cartItems.length === 0) {
      showCustomToast({ type: 'error', message: 'Your cart is empty' });
      return;
    }
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    window.location.href = '/shop';
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="cart-empty-state">
            <div className="cart-empty-icon">
              <i className="fas fa-spinner fa-spin fa-4x"></i>
            </div>
            <p style={{ fontSize: '18px', marginTop: '1rem' }}>Loading your cart...</p>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="cart-empty-state">
            <div className="cart-empty-icon">
              <i className="fas fa-shopping-cart fa-4x"></i>
            </div>
            <h2 className="cart-empty-title">Your cart is empty</h2>
            <p className="cart-empty-description">
              Looks like you haven't added any items to your cart yet.
            </p>
            <button className="cart-continue-shopping-btn" onClick={handleContinueShopping}>
              <i className="fas fa-shopping-bag"></i>
              Start Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        {/* Breadcrumb */}
        <div className="cart-breadcrumb">
          <a href="/">Home</a>
          <span>/</span>
          <span>Shopping Cart</span>
        </div>

        {/* Page Title */}
        <div className="cart-header">
          <h1 className="cart-title">Shopping Cart</h1>
          <p className="cart-subtitle">{getTotalItems()} item{getTotalItems() !== 1 ? 's' : ''} in your cart</p>
        </div>

        {/* Main Grid */}
        <div className="cart-main-grid">
          {/* Cart Items Section */}
          <div className="cart-items-section">
            <div className="cart-items-list">
              {cartItems.map((item) => (
                <div key={item.id} className="cart-item">
                  <div className="cart-item-content">
                    {/* Item Selection Checkbox (matching mobile app functionality) */}
                    <div style={{ padding: '0 0.5rem', display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={item.selected || false}
                        onChange={() => toggleSelection(item.id)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                          accentColor: '#007bff'
                        }}
                        title="Select item for checkout"
                      />
                    </div>
                    <div className="cart-item-image">
                      <img src={item.image} alt={item.name} />
                    </div>

                    <div className="cart-item-details">
                      <div className="cart-item-info">
                        <h3 className="cart-item-name">{item.name}</h3>
                        <p className="cart-item-description">{item.description}</p>
                        <div className="cart-item-price-mobile">
                          {formatPrice(item.price)}
                        </div>
                      </div>

                      <div className="cart-item-actions">
                        <div className="cart-quantity-controls">
                          <button
                            className="cart-quantity-btn"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            <i className="fas fa-minus"></i>
                          </button>
                          <span className="cart-quantity-value">{item.quantity}</span>
                          <button
                            className="cart-quantity-btn"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>

                        <div className="cart-item-secondary-actions">
                          <button
                            className="cart-save-later-btn"
                            onClick={() => saveForLater(item.id)}
                            title="Save for later"
                          >
                            <i className="fas fa-heart"></i>
                          </button>

                          <button
                            className="cart-remove-btn"
                            onClick={() => removeItem(item.id)}
                            title="Remove item"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="cart-item-price-desktop">
                      {formatPrice(item.price * item.quantity)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Promo Code Section */}
            <div className="cart-promo-section">
              <h3>Promo Code</h3>
              {appliedPromo ? (
                <div className="applied-promo">
                  <div className="applied-promo-info">
                    <i className="fas fa-tag"></i>
                    <span>Code: {appliedPromo.code}</span>
                    <span className="promo-description">({appliedPromo.description})</span>
                  </div>
                  <button className="remove-promo-btn" onClick={removePromoCode}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                <div className="promo-input-group">
                  <input
                    type="text"
                    className="promo-input"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  />
                  <button className="promo-apply-btn" onClick={applyPromoCode} disabled={applyingPromo}>
                    {applyingPromo ? 'Applying…' : 'Apply'}
                  </button>
                </div>
              )}
            </div>

          </div>

          {/* Cart Summary Section */}
          <div className="cart-summary">
            <h3 className="cart-summary-title">Order Summary</h3>

            <div className="cart-summary-content">
              <div className="cart-total-row">
                <span className="cart-total-label">Subtotal ({getTotalItems()} items)</span>
                <span className="cart-total-value">{formatPrice(calculateSubtotal())}</span>
              </div>

              {appliedPromo && (
                <div className="cart-total-row discount-row">
                  <span className="cart-total-label">
                    Discount ({appliedPromo.code})
                  </span>
                  <span className="cart-total-value discount">
                    -{formatPrice(calculateDiscount())}
                  </span>
                </div>
              )}

              <div className="cart-total-row">
                <span className="cart-total-label">
                  Logistics Charge
                  {calculateShipping() > 0 && ` (VAT ${vatPercentage}% included)`}
                  {calculateShipping() > 0 && realShippingFee == null && !cartData.shipping && (
                    <span style={{ fontSize: '0.72rem', marginLeft: '0.4rem', fontWeight: 700, color: '#F59E0B' }}>
                      est.
                    </span>
                  )}
                </span>
                <span className="cart-total-value">{formatPrice(calculateLogisticsCharge())}</span>
              </div>

              {calculateSubtotal() > 0 && (
                <div className="cart-total-row">
                  <span className="cart-total-label">
                    Escrow Fee ({escrowFeePercentage}%)
                    <span style={{ fontSize: '0.72rem', marginLeft: '0.4rem', fontWeight: 700, color: '#F59E0B' }}>
                      est.
                    </span>
                  </span>
                  <span className="cart-total-value">{formatPrice(calculateEscrowFeeAmount())}</span>
                </div>
              )}

              <div className="cart-total-row cart-final-total">
                <span className="cart-total-label">Total</span>
                <span className="cart-total-value">{formatPrice(calculateTotal())}</span>
              </div>
            </div>

            <div className="cart-summary-actions">
              <button
                className="cart-checkout-btn"
                onClick={handleProceedToCheckout}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-lock"></i>
                    Secure Checkout
                  </>
                )}
              </button>

              <button className="cart-continue-shopping-btn" onClick={handleContinueShopping}>
                <i className="fas fa-arrow-left"></i>
                Continue Shopping
              </button>
            </div>

            {/* Security Badge */}
            <div className="cart-security-badge">
              <i className="fas fa-shield-alt"></i>
              <span>Secure SSL encryption</span>
            </div>
          </div>
        </div>

        {/* Saved for Later Section */}
        {savedForLater.length > 0 && (
          <div className="cart-saved-items-section">
            <h3 className="saved-items-title">
              <i className="fas fa-heart"></i>
              Saved for Later ({savedForLater.length} items)
            </h3>
            <div className="saved-items-list">
              {savedForLater.map((item) => (
                <div key={`saved-${item.id}`} className="saved-item">
                  <div className="saved-item-image">
                    <img src={item.image} alt={item.name} />
                  </div>
                  <div className="saved-item-details">
                    <h4 className="saved-item-name">{item.name}</h4>
                    <p className="saved-item-description">{item.description}</p>
                    <div className="saved-item-price">{formatPrice(item.price)}</div>
                  </div>
                  <div className="saved-item-actions">
                    <button
                      className="move-to-cart-btn"
                      onClick={() => moveToCart(item.id)}
                    >
                      <i className="fas fa-shopping-cart"></i>
                      Move to Cart
                    </button>
                    <button
                      className="remove-saved-btn"
                      onClick={() => removeSavedItem(item.id)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recently Viewed Section */}
        <div className="cart-recently-viewed">
          <h3>You might also like</h3>
          <div className="recently-viewed-items">
            <div className="recently-viewed-item">
              <img src="https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=200&auto=format&fit=crop&q=60" alt="Product" />
              <h4>iPhone 15 Pro</h4>
              <span>{formatPrice(1200000)}</span>
            </div>
            <div className="recently-viewed-item">
              <img src="https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=200&auto=format&fit=crop&q=60" alt="Product" />
              <h4>OnePlus 12</h4>
              <span>{formatPrice(650000)}</span>
            </div>
            <div className="recently-viewed-item">
              <img src="https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?w=200&auto=format&fit=crop&q=60" alt="Product" />
              <h4>iPad Pro</h4>
              <span>{formatPrice(800000)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;